import { RENDERING_CONTEXT } from '@bedrock-rdk/constants';
import _merge from 'lodash/merge';
import { Lightning } from '@lightningjs/sdk';
import { getProgress } from '../progress/progress.utils';
import {
  areContentItemsEqual,
  areContextsEqual,
  getTemplateEntry,
  ICON_POSITION,
  makeTextPatch,
} from '../utils/templates';
import { TRANSPARENT } from '../utils/colors';
import Incentive from '../incentive/incentive.component';
import { getLinesCount } from '../incentive/incentive.utils';
import { ALIGNMENT_VERTICAL } from '../incentive/incentive.constants';
import ImageShader from '../shaders/image.shader';
import FocusShader from '../shaders/focus.shader';
import { DETACHED_SIGNAL, INACTIVE_SIGNAL, SELECTED_SIGNAL } from '../signals';
import { shouldDisplayTreatment } from './poster.utils';
import {
  getInitialStyle,
  getIconViewBox,
  colors,
  getDetailsPosition,
  defaultContext,
  PADDING_LEFT,
  PADDING_BOTTOM,
  getFocusedStyle,
  unFocusedStyle,
  getIcon,
  IMAGE_OFFSET,
  ICON_PADDING_HORIZONTAL,
  ICON_HEIGHT,
} from './poster.style';
import {
  templates,
  sizes,
  imageOptions,
  TITLES_ORDER,
  DISPLAY_TITLE,
  POSTER_OVERLAY,
  ICONS_TYPES,
  DISPLAY_EXTRA_TITLE,
  DISPLAY_DETAILS,
  DISPLAY_ICONS,
} from './poster.constants';

/**
 * This item display programs or episodes. It's a 16:9 ratio format.
 *
 * @version v2.8.3
 * @see https://zeroheight.com/726d5c282/p/700d1a-poster
 */
export default class Poster extends Lightning.Component {
  static _template() {
    return _merge(
      {
        Border: {
          rect: true,
          shader: {
            type: FocusShader,
          },
        },
        Image: {
          shader: {
            type: ImageShader,
          },
        },
        TopLeftIcon: {
          type: ICONS_TYPES[ICON_POSITION.TOP_LEFT],
        },
        TopRightIcon: {
          type: ICONS_TYPES[ICON_POSITION.TOP_RIGHT],
        },
        BottomRightIcon: {
          type: ICONS_TYPES[ICON_POSITION.BOTTOM_RIGHT],
        },
        ExtraTitle: {},
        Title: {},
        Details: {},
        Highlights: {},
        Incentive: {
          type: Incentive,
        },
      },
      getInitialStyle(),
    );
  }

  _construct() {
    this._index = 0;
    this._style = { ...defaultContext };
  }

  _inactive() {
    this.signal(INACTIVE_SIGNAL, this);
  }

  _detach() {
    this._item = undefined;
    this.signal(DETACHED_SIGNAL, this);
  }

  get index() {
    return this._index;
  }

  set index(value) {
    this._index = value;
  }

  get item() {
    return this._item;
  }

  set item({
    content,
    config,
    template,
    colorScheme = this._style.colorMode,
    renderingContext = RENDERING_CONTEXT.GENERAL,
  }) {
    const progress = getProgress(content.progress);

    if (areContentItemsEqual(this._item, content) && areContextsEqual(this._renderingContext, renderingContext)) {
      this._item = content;
      // A three level patch object to move one number: the shader is already there, so it is written directly
      this.tag('Image').shader.progress = progress;

      return;
    }

    this._renderingContext = renderingContext;
    this._item = content;
    if (colorScheme && colorScheme !== this._style.colorMode) {
      this._style = { screenSize: this._style.screenSize, colorMode: colorScheme };
    }

    const { screenSize } = this._style;
    this._config = config || getTemplateEntry(sizes, template, renderingContext, screenSize);

    const { title, extraTitle, highlight, pictos, incentives, image } = this._item;
    const viewBox = getIconViewBox(screenSize);
    const topLeftIcon = getIcon(pictos, ICON_POSITION.TOP_LEFT);
    const topRightIcon = getIcon(pictos, ICON_POSITION.TOP_RIGHT);
    const bottomRightIcon = getIcon(pictos, ICON_POSITION.BOTTOM_RIGHT);
    const details = DISPLAY_DETAILS ? this._item.details : '';
    const wordWrapWidth =
      this._config.w -
      PADDING_LEFT[screenSize] -
      (bottomRightIcon ? ICON_HEIGHT[screenSize] + ICON_PADDING_HORIZONTAL * 2 : PADDING_LEFT[screenSize]);
    const wordWrapWidthFlexible = details ? Math.round(wordWrapWidth / 2) : wordWrapWidth;
    const imageUrl = (image && image.src) || '';
    const textColor = colors.text[colorScheme];
    const borderOffset = IMAGE_OFFSET[screenSize];
    const incentiveLines = getLinesCount(incentives);

    this.patch({
      Border: {
        w: this._config.w + borderOffset,
        h: this._config.h + borderOffset,
      },
      Image: {
        w: this._config.w,
        h: this._config.h,
        src: imageUrl,
        shader: {
          progress,
          overlayColor: shouldDisplayTreatment(this._item) ? POSTER_OVERLAY[this._style.colorMode] : TRANSPARENT,
        },
      },
      ExtraTitle: makeTextPatch(DISPLAY_EXTRA_TITLE ? extraTitle : '', wordWrapWidth, textColor),
      Title: makeTextPatch(DISPLAY_TITLE ? title : '', wordWrapWidth, textColor),
      Details: makeTextPatch(details, wordWrapWidthFlexible, colors.details[colorScheme]),
      Highlights: makeTextPatch(highlight, wordWrapWidthFlexible, colors.highlight[colorScheme]),
      TopLeftIcon: {
        visible: DISPLAY_ICONS && topLeftIcon,
        iconData: {
          picto: topLeftIcon,
          viewBox,
        },
      },
      TopRightIcon: {
        visible: DISPLAY_ICONS && topRightIcon,
        iconData: {
          picto: topRightIcon,
          viewBox,
        },
      },
      BottomRightIcon: {
        visible: DISPLAY_ICONS && bottomRightIcon,
        iconData: {
          picto: bottomRightIcon,
          viewBox,
        },
      },
      Incentive: {
        visible: incentiveLines > 0,
        data: {
          w: this._config.w,
          linesCount: incentiveLines,
          incentives,
          colorMode: this._style.colorMode,
          alignment: ALIGNMENT_VERTICAL,
        },
      },
    });

    this._updateMetadataPosition();
  }

  _updateMetadataPosition() {
    // NOTE: we are using manual calculation instead of the "flex" layout in order to speed up tile creation
    const paddingBottom = PADDING_BOTTOM[this._style.screenSize];
    const paddingLeft = PADDING_LEFT[this._style.screenSize];
    let offset = this._config.h - paddingBottom;

    const [firstLineTag, secondLineTag] = TITLES_ORDER;

    const firstLine = this.tag(firstLineTag);
    const secondLine = this.tag(secondLineTag);
    const details = this.tag('Details');
    const highlights = this.tag('Highlights');
    const bottomRightIcon = this.tag('BottomRightIcon');

    if (details.visible || highlights.visible) {
      offset -= details.text.lineHeight;
      details.patch(getDetailsPosition(this._style.screenSize, highlights.visible, Boolean(bottomRightIcon.name)));
      details.y = offset;
      highlights.x = paddingLeft;
      highlights.y = offset;
    }

    if (secondLine.visible) {
      offset -= secondLine.text.lineHeight;
      secondLine.x = paddingLeft;
      secondLine.y = offset;
    }

    if (firstLine.visible) {
      offset -= firstLine.text.lineHeight;
      firstLine.x = paddingLeft;
      firstLine.y = offset;
    }
  }

  _focus() {
    this.patch(getFocusedStyle(this._style));
  }

  _unfocus() {
    this.patch(unFocusedStyle);
  }

  _handleEnter() {
    this.signal(SELECTED_SIGNAL, this._item);

    return true;
  }
}

Poster.Templates = templates;
Poster.Sizes = sizes;
Poster.ImageOptions = imageOptions;
